import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  DesignSystemProvider,
  Toaster,
  type DesignSystemProviderProps,
} from '@rammetec/design-system'
import { toUiPreferences } from '@rammetec/app-kit/preferences'
import {
  AuthServiceProvider,
  bootstrapLogService,
  RepositoryResolverProvider,
  SessionProvider,
  SystemApiWsProvider,
  ThemePreferenceProvider,
  useCurrentUser,
  useSessionManager,
  useThemePreference,
} from '@syscolect/application'
import { rtLogService } from '@rammetec/core'
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'
import { appInfo } from '@/config/appInfo'
import { env } from '@/config/env'
import { authService } from '@/services/authService'
import { bootstrapDataAccess } from '@/services/data-access.bootstrap'
import { getLogContextOrgId, setLogContextOrgId } from '@/services/logContextStore'
import { createConsoleLogHttpTransport } from '@/services/logHttpTransport'
import { fetchLogConfig } from '@/services/logApi'
import { preferencesService } from '@/services/preferences.bootstrap'
import { themePreferenceStorage } from '@/services/themePreferenceStorage'
import { systemApiWsStore } from '@/services/system-api-ws.bootstrap'
import { ConfigGate } from './ConfigGate'
import { ModuleResolverBridge } from './modules/ModuleResolverBridge'
import { PreferencesBridge } from './PreferencesBridge'

const LOG_LEVEL_POLL_MS = 10 * 60 * 1000
const logPersistEnabled =
  env.appEnvironment !== 'development' || env.logPersistInDev

bootstrapDataAccess() // module-level: fail loud on bad Firebase config

bootstrapLogService({
  transport: createConsoleLogHttpTransport(),
  app: 'sc-console',
  appVersion: appInfo.version,
  getContext: () => ({
    orgId: getLogContextOrgId() ?? undefined,
    route: typeof location !== 'undefined' ? location.pathname : undefined,
  }),
  persist: env.appEnvironment !== 'development' || env.logPersistInDev,
})

/** Keeps `getContext().orgId` in sync with the selected organization (sync read). */
function LogContextBridge({ children }: { children: ReactNode }) {
  const { currentOrganizationId } = useSessionManager()
  useEffect(() => {
    setLogContextOrgId(currentOrganizationId)
  }, [currentOrganizationId])
  return children
}

/**
 * Applies org debug grants to `rtLogService.setMinLevel` (request 0350).
 * Polls every 10 min; schedules expiry from `until`. Failures stay at `info`
 * and only log at `debug` so a down API does not flood persisted errors.
 */
function LogLevelBridge({ children }: { children: ReactNode }) {
  const { currentOrganizationId } = useSessionManager()
  const { currentUserId } = useCurrentUser()

  useEffect(() => {
    if (!logPersistEnabled || !currentUserId || !currentOrganizationId) {
      rtLogService.setMinLevel('info')
      return
    }

    let cancelled = false
    let expireTimer: ReturnType<typeof setTimeout> | undefined
    let pollTimer: ReturnType<typeof setInterval> | undefined

    const clearExpire = () => {
      if (expireTimer !== undefined) {
        clearTimeout(expireTimer)
        expireTimer = undefined
      }
    }

    const applyConfig = async () => {
      clearExpire()
      rtLogService.setMinLevel('info')
      try {
        const config = await fetchLogConfig(currentOrganizationId)
        if (cancelled) return
        if (config.minLevel === 'debug') {
          rtLogService.setMinLevel('debug')
          if (config.until) {
            const ms = Date.parse(config.until) - Date.now()
            if (Number.isFinite(ms) && ms > 0) {
              expireTimer = setTimeout(() => {
                rtLogService.setMinLevel('info')
              }, ms)
            } else {
              rtLogService.setMinLevel('info')
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          rtLogService.setMinLevel('info')
          rtLogService.debug('[log-level] failed to fetch /log/v1/config', { error })
        }
      }
    }

    void applyConfig()
    pollTimer = setInterval(() => {
      void applyConfig()
    }, LOG_LEVEL_POLL_MS)

    return () => {
      cancelled = true
      clearExpire()
      if (pollTimer !== undefined) clearInterval(pollTimer)
      rtLogService.setMinLevel('info')
    }
  }, [currentUserId, currentOrganizationId])

  return children
}

/** Sync UiPreferences adapter over the preferences singleton (works pre-hydrate). */
const uiPreferences = toUiPreferences(preferencesService)

function SystemApiWsBridge({ children }: { children: ReactNode }) {
  const client = useSyncExternalStore(systemApiWsStore.subscribe, systemApiWsStore.getClient)

  useEffect(() => {
    void systemApiWsStore.connect()
  }, [])

  return <SystemApiWsProvider value={client}>{children}</SystemApiWsProvider>
}

function ThemedDesignSystem({
  brand,
  children,
}: {
  brand: DesignSystemProviderProps['brand']
  children: ReactNode
}) {
  const { mode } = useThemePreference()
  return (
    <DesignSystemProvider brand={brand} mode={mode} uiPreferences={uiPreferences}>
      {children}
    </DesignSystemProvider>
  )
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => new QueryClient())
  return (
    <ThemePreferenceProvider initial={env.designSystemMode} storage={themePreferenceStorage}>
      <ThemedDesignSystem brand={env.designSystemBrand}>
        <ConfigGate>
          <QueryClientProvider client={queryClient}>
            <AuthServiceProvider value={authService}>
              <SystemApiWsBridge>
                <SessionProvider>
                  <LogContextBridge>
                    <LogLevelBridge>
                      <PreferencesBridge>
                        <RepositoryResolverProvider>
                          <ModuleResolverBridge>
                            {children}
                            {/* richColors → error toasts get a red accent (success green, etc.) */}
                            <Toaster richColors />
                          </ModuleResolverBridge>
                        </RepositoryResolverProvider>
                      </PreferencesBridge>
                    </LogLevelBridge>
                  </LogContextBridge>
                </SessionProvider>
              </SystemApiWsBridge>
            </AuthServiceProvider>
          </QueryClientProvider>
        </ConfigGate>
      </ThemedDesignSystem>
    </ThemePreferenceProvider>
  )
}
